import "core-js/stable";
import "regenerator-runtime/runtime";

import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Switch, Route, Redirect } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import {
  hasAtLeastOneRole,
  hasRole,
  useActiveRoles,
} from "./utils/permissionsUtils.tsx";
import { routesConfig } from "./routesConfig";
import { AccessDenied } from "./components/AccessDenied.tsx";
import { RoleEnum } from "./types/RoleEnum.ts";
import Settings from "./settings";
import MyPublisher from "./routes/my-publisher/MyPublisher.tsx";
import { AdminResolutionModal } from "./components/AdminResolutionModal.tsx";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    },
  },
});

const BrPublishers = (props) => {
  const { roles: activeRoles, activeOrganization } = useActiveRoles();
  const [adminResolutionDismissed, setAdminResolutionDismissed] =
    useState(false);

  const handleAdminResolved = useCallback(() => {
    window.location.reload();
  }, []);

  const handleAdminDismissed = useCallback(() => {
    setAdminResolutionDismissed(true);
  }, []);

  const {
    showSettings,
    match: { path },
  } = props;

  if (showSettings) {
    return <Settings {...props} />;
  }

  // Redirect users who haven't selected an organization yet
  // When no organization is selected, activeRoles is an empty array
  const needsOrgSelection =
    Array.isArray(activeRoles) && activeRoles.length === 0;

  if (needsOrgSelection) {
    return <Redirect to="/brusers/my-profile/select-organization" />;
  }

  const showAdminResolution =
    !adminResolutionDismissed &&
    activeOrganization &&
    activeOrganization.adminResolved === false &&
    hasRole(activeRoles, RoleEnum.PUBLISHER_ADMIN);

  return (
    <QueryClientProvider client={queryClient}>
      {showAdminResolution && (
        <AdminResolutionModal
          activeOrganization={activeOrganization}
          onResolved={handleAdminResolved}
          onDismiss={handleAdminDismissed}
        />
      )}
      <Switch>
        {Object.keys(routesConfig).map((key) => {
          const {
            path: routePath,
            roles,
            component: Component,
          } = routesConfig[key];

          const isSuperUser = hasRole(activeRoles, RoleEnum.SUPERUSER);
          const hasRequiredRole = roles
            ? hasAtLeastOneRole(activeRoles, roles)
            : true;
          const hasAccess = isSuperUser || hasRequiredRole;

          // Special case: /publishers route shows MyPublisher for non-agency workers
          if (key === "publishers") {
            const canViewPublishers =
              isSuperUser || hasRole(activeRoles, RoleEnum.AGENCY_WORKER);

            return (
              <Route
                key={key}
                path={`${path}${routePath}`}
                render={() =>
                  canViewPublishers ? <Component /> : <MyPublisher />
                }
              />
            );
          }

          return (
            <Route
              key={key}
              path={`${path}${routePath}`}
              render={() =>
                !hasAccess ? <AccessDenied showNavigation /> : <Component />
              }
            />
          );
        })}

        {/* Default Route */}
        <Route
          render={() => {
            const isSuperUser = hasRole(activeRoles, RoleEnum.SUPERUSER);

            if (isSuperUser) {
              return <Redirect to={`${path}/publishers`} />;
            }

            const accessibleRoute = Object.values(routesConfig).find(
              (route) => {
                if (!route.roles) return true;
                return hasAtLeastOneRole(activeRoles, route.roles);
              },
            );

            if (accessibleRoute) {
              return <Redirect to={`${path}${accessibleRoute.path}`} />;
            }

            return <AccessDenied showNavigation />;
          }}
        />
      </Switch>
    </QueryClientProvider>
  );
};

BrPublishers.propTypes = {
  showSettings: PropTypes.bool,
  match: PropTypes.shape({
    path: PropTypes.string.isRequired,
  }).isRequired,
};

export default BrPublishers;
